import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const EN2ES_PLANETS = {
  Sun: 'Sol', Moon: 'Luna', Mercury: 'Mercurio', Venus: 'Venus',
  Mars: 'Marte', Jupiter: 'Jupiter', Saturn: 'Saturno',
  Uranus: 'Urano', Neptune: 'Neptuno', Pluto: 'Pluton',
  True_North_Lunar_Node: 'Nodo Norte', Chiron: 'Quiron',
  Ascendant: 'Ascendente', Mean_Lilith: 'Lilith Media',
  True_South_Lunar_Node: 'Nodo Sur', Descendant: 'Descendente',
  Medium_Coeli: 'MC', Imum_Coeli: 'IC', Mean_Node: 'Nodo Norte',
  Part_of_Fortune: 'Parte de la Fortuna',
};
const ASPECT_NAMES = {
  conjunction: 'Conjuncion', opposition: 'Oposicion', trine: 'Trigono',
  square: 'Cuadratura', sextile: 'Sextil', quincunx: 'Quincuncio',
  'semi-sextile': 'Semi-sextil', 'semi-square': 'Semi-cuadratura',
  sesquiquadrate: 'Sesquicuadratura', quintile: 'Quintil',
};
const ASPECT_COLORS = {
  conjunction: '#D4A017', trine: '#0000EE', sextile: '#0000EE',
  square: '#CC0000', opposition: '#880088', quincunx: '#009900',
  'semi-sextile': '#009900', 'semi-square': '#CC6600',
  sesquiquadrate: '#CC6600', quintile: '#666',
};

export default function AspectsTable({ aspects }) {
  const [showAll, setShowAll] = useState(false);
  const topAspects = useMemo(() => aspects.filter(a => a.draw_on_wheel), [aspects]);
  const restAspects = useMemo(() => aspects.filter(a => !a.draw_on_wheel), [aspects]);
  const maxScore = aspects[0]?.score || 1;

  const renderRow = (a, i) => {
    const color = ASPECT_COLORS[a.aspect] || '#888';
    const pct = a.score ? Math.round((a.score / maxScore) * 100) : 0;
    return (
      <div key={i} className="flex items-center bg-black/30 rounded-lg px-2 sm:px-3 py-2 border border-gold-300/8 text-xs sm:text-sm gap-2 min-w-0" data-testid={`aspect-row-${i}`}>
        <div className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
        <span className="text-[var(--text-secondary)] truncate w-20 sm:w-24">{EN2ES_PLANETS[a.planet1] || a.planet1}</span>
        <span className="font-medium shrink-0 text-[10px] sm:text-xs px-1.5 py-0.5 rounded" style={{ color, background: `${color}15` }}>
          {ASPECT_NAMES[a.aspect] || a.aspect}
        </span>
        <span className="text-[var(--text-secondary)] truncate w-20 sm:w-24">{EN2ES_PLANETS[a.planet2] || a.planet2}</span>
        <span className="text-[var(--text-muted)] text-[10px] shrink-0 w-10 text-right">{a.orbit}°</span>
        <span className={`text-[10px] shrink-0 w-10 text-center font-medium ${a.movement === 'Applying' ? 'text-emerald-400' : 'text-amber-400'}`}>
          {a.movement === 'Applying' ? 'App' : a.movement === 'Separating' ? 'Sep' : '-'}
        </span>
        <div className="hidden sm:block w-16 shrink-0">
          <div className="h-1.5 bg-black/50 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color, opacity: 0.7 }} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-6" data-testid="aspects-table">
      <h4 className="text-lg font-semibold text-purple-300 mb-1">Aspectos Planetarios</h4>
      <p className="text-[var(--text-muted)] text-xs mb-3">Ordenados por importancia. <span className="text-emerald-400">App</span> = Aplicando (activo) | <span className="text-amber-400">Sep</span> = Separando</p>
      <div className="space-y-1.5">
        {topAspects.map((a, i) => renderRow(a, i))}
      </div>
      {restAspects.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowAll(!showAll)} className="flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 transition-colors" data-testid="show-more-aspects-btn">
            {showAll ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {showAll ? 'Ocultar' : `Ver ${restAspects.length} aspectos mas`}
          </button>
          {showAll && (
            <div className="space-y-1.5 mt-2">
              {restAspects.map((a, i) => renderRow(a, topAspects.length + i))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
